import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOpportunities } from '../contexts/OpportunityContext';
import LoadingSpinner from '../components/LoadingSpinner';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import {
  AdjustmentsHorizontalIcon,
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  MapPinIcon,
  SparklesIcon,
  TagIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { opportunityAPI } from '../services/api';

const emptyPreferences = {
  procuringOrganizations: [] as string[],
  procuringTypes: [] as string[],
  industries: [] as string[],
  keywords: [] as string[],
};

type PreferenceKey = keyof typeof emptyPreferences;


const preferenceMetadata: Record<PreferenceKey, {
  label: string;
  description: string;
  placeholder?: string;
  icon: React.ReactNode;
  inputType: 'text' | 'multiselect';
}> = {
  procuringOrganizations: {
    label: 'Procuring Organizations',
    description: 'Stay in the loop for bids from the municipalities and agencies that matter most to you.',
    placeholder: 'Select organizations',
    icon: <MapPinIcon className="h-5 w-5 text-blue-500" />,
    inputType: 'multiselect',
  },
  procuringTypes: {
    label: 'Organization Types',
    description: 'Choose the kinds of public organizations you want to work with, such as municipalities or state agencies.',
    placeholder: 'Select organization types',
    icon: <BuildingLibraryIcon className="h-5 w-5 text-blue-500" />,
    inputType: 'multiselect',
  },
  industries: {
    label: 'Industries',
    description: 'Focus your feed on the work you do best by naming your target industries.',
    placeholder: 'Select industries',
    icon: <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />,
    inputType: 'multiselect',
  },
  keywords: {
    label: 'Keywords',
    description: 'Use keywords to capture materials, project types, or agencies you care about.',
    placeholder: 'Add a keyword...',
    icon: <TagIcon className="h-5 w-5 text-blue-500" />,
    inputType: 'text',
  },
};

const Preferences: React.FC = () => {
  const { user } = useAuth();
  const { premiumPreferences, loadPremiumPreferences, savePremiumPreferences, filters } = useOpportunities();

  const isPremium = Boolean(user?.is_premium);
  const [preferences, setPreferences] = useState(emptyPreferences);
  const [draftKeyword, setDraftKeyword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [canonicalFilters, setCanonicalFilters] = useState<{
    industries: string[];
    procuring_organizations?: string[];
    procuring_types?: string[];
  } | null>(null);
  const [canonicalLoading, setCanonicalLoading] = useState(true);

  useEffect(() => {
    if (!isPremium) {
      return;
    }

    let isMounted = true;
    const fetchPreferences = async () => {
      try {
        setIsLoading(true);
        await loadPremiumPreferences();
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPreferences();
    return () => {
      isMounted = false;
    };
  }, [isPremium, loadPremiumPreferences]);

  useEffect(() => {
    if (isPremium) {
      setPreferences({
        procuringOrganizations: [...premiumPreferences.procuringOrganizations],
        procuringTypes: [...premiumPreferences.procuringTypes],
        industries: [...premiumPreferences.industries],
        keywords: [...premiumPreferences.keywords],
      });
    } else {
      setPreferences(emptyPreferences);
    }
  }, [isPremium, premiumPreferences]);

  useEffect(() => {
    if (status) {
      const timeout = window.setTimeout(() => setStatus(null), 5000);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [status]);

  useEffect(() => {
    let isMounted = true;
    const fetchCanonicalFilters = async () => {
      try {
        const response = await opportunityAPI.getCanonicalFilters();
        if (!isMounted) {
          return;
        }
        setCanonicalFilters({
          industries: response.data.industries ?? [],
          procuring_organizations: response.data.procuring_organizations ?? response.data.cities ?? [],
          procuring_types: response.data.procuring_types ?? [],
        });
      } catch (error) {
        console.error('Failed to load canonical filters', error);
      } finally {
        if (isMounted) {
          setCanonicalLoading(false);
        }
      }
    };
    fetchCanonicalFilters();
    return () => {
      isMounted = false;
    };
  }, []);

  const canonicalIndustryOptions = useMemo(() => canonicalFilters?.industries ?? [], [canonicalFilters]);
  const canonicalOrganizationOptions = useMemo(
    () => canonicalFilters?.procuring_organizations ?? [],
    [canonicalFilters]
  );
  const canonicalOrganizationTypeOptions = useMemo(
    () => canonicalFilters?.procuring_types ?? [],
    [canonicalFilters]
  );

  const organizationOptions = useMemo(() => {
    const source =
      canonicalOrganizationOptions.length > 0
        ? canonicalOrganizationOptions
        : filters?.procuringOrganizations ?? [];
    const optionSet = new Set(source);
    preferences.procuringOrganizations.forEach((org) => optionSet.add(org));
    return Array.from(optionSet).sort((a, b) => a.localeCompare(b));
  }, [canonicalOrganizationOptions, filters, preferences.procuringOrganizations]);

  const industryOptions = useMemo(() => {
    const source = canonicalIndustryOptions.length > 0 ? canonicalIndustryOptions : filters?.industries ?? [];
    const optionSet = new Set(source);
    preferences.industries.forEach((industry) => optionSet.add(industry));
    return Array.from(optionSet).sort((a, b) => a.localeCompare(b));
  }, [canonicalIndustryOptions, filters, preferences.industries]);

  const organizationTypeOptions = useMemo(() => {
    const source =
      canonicalOrganizationTypeOptions.length > 0
        ? canonicalOrganizationTypeOptions
        : filters?.procuringTypes ?? [];
    const optionSet = new Set(source);
    preferences.procuringTypes.forEach((type) => optionSet.add(type));
    return Array.from(optionSet).sort((a, b) => a.localeCompare(b));
  }, [canonicalOrganizationTypeOptions, filters, preferences.procuringTypes]);

  const industriesLoading = canonicalLoading && canonicalIndustryOptions.length === 0;
  const organizationsLoading = canonicalLoading && canonicalOrganizationOptions.length === 0;
  const organizationTypesLoading = canonicalLoading && canonicalOrganizationTypeOptions.length === 0;

  const multiSelectOptions = {
    procuringOrganizations: organizationOptions,
    procuringTypes: organizationTypeOptions,
    industries: industryOptions,
  } as const;

  const changesPending = useMemo(() => {
    const preferenceChanged =
      JSON.stringify(preferences.procuringOrganizations) !== JSON.stringify(premiumPreferences.procuringOrganizations) ||
      JSON.stringify(preferences.procuringTypes) !== JSON.stringify(premiumPreferences.procuringTypes) ||
      JSON.stringify(preferences.industries) !== JSON.stringify(premiumPreferences.industries) ||
      JSON.stringify(preferences.keywords) !== JSON.stringify(premiumPreferences.keywords);


    return preferenceChanged;
  }, [preferences, premiumPreferences]);

  const handleAddKeyword = () => {
    const value = draftKeyword.trim();
    if (!value) {
      return;
    }

    setPreferences((prev) => {
      if (prev.keywords.some((existing) => existing.toLowerCase() === value.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        keywords: [...prev.keywords, value],
      };
    });

    setDraftKeyword('');
  };

  const handleRemovePreference = (key: PreferenceKey, value: string) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item !== value),
    }));
  };

  const handleSelectChange = (key: Extract<PreferenceKey, 'procuringOrganizations' | 'procuringTypes' | 'industries'>, values: string[]) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: values,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatus(null);
    try {
      await savePremiumPreferences({
        procuringOrganizations: preferences.procuringOrganizations,
        procuringTypes: preferences.procuringTypes,
        industries: preferences.industries,
        keywords: preferences.keywords,
      });
      setStatus({ type: 'success', message: 'Preferences saved. Your feed will update automatically.' });
    } catch (error) {
      console.error('Save preferences error:', error);
      setStatus({ type: 'error', message: 'Unable to save preferences right now. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences({
      procuringOrganizations: [...premiumPreferences.procuringOrganizations],
      procuringTypes: [...premiumPreferences.procuringTypes],
      industries: [...premiumPreferences.industries],
      keywords: [...premiumPreferences.keywords],
    });
    setDraftKeyword('');
  };

  if (!user) {
    return <LoadingSpinner message="Loading Match Preferences..." />;
  }

  if (!isPremium) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Match Preferences</h1>
            <p className="mt-2 max-w-2xl text-base text-slate-600">
              Upgrade to Pro to save the procuring organizations, industries, and keywords that power your personalised feed and email
              alerts.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-6 text-sm text-blue-700">
          Preference management is available for Pro accounts. Visit your{' '}
          <Link to="/account" className="font-semibold underline decoration-blue-300 underline-offset-4">
            account page
          </Link>{' '}
          to upgrade and unlock the feature set.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Match Preferences</h1>
          <p className="mt-2 max-w-2xl text-base text-slate-600">
            Tune the signals that matter most. These preferences flow into your personalised dashboard and email alerts.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          <SparklesIcon className="h-4 w-4" />
          Pro
        </span>
      </header>

      {status && (
        <div
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium ${
            status.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          <CheckCircleIcon className={`h-5 w-5 ${status.type === 'success' ? 'text-emerald-500' : 'text-rose-500'}`} />
          {status.message}
        </div>
      )}

      <section className="space-y-6">
        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              Fetching your saved preferences...
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {(Object.keys(preferenceMetadata) as PreferenceKey[]).map((key) => {
              const meta = preferenceMetadata[key];

              return (
                <div key={key} className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      {meta.icon}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{meta.label}</h2>
                      <p className="mt-1 text-sm text-slate-500">{meta.description}</p>
                    </div>
                  </div>

                  <div className="mt-auto space-y-3 pt-6">
                    {key === 'procuringOrganizations' || key === 'procuringTypes' || key === 'industries' ? (
                      <>
                        <MultiSelectDropdown
                          label={meta.label}
                          placeholder={meta.placeholder ?? `Select ${meta.label}`}
                          options={multiSelectOptions[key]}
                          values={preferences[key]}
                          onChange={(values) => handleSelectChange(key, values)}
                          disabled={multiSelectOptions[key].length === 0}
                          searchable
                        />
                        {((key === 'industries' && (industriesLoading || multiSelectOptions[key].length === 0)) ||
                          (key === 'procuringOrganizations' && (organizationsLoading || multiSelectOptions[key].length === 0))) && (
                          <p className="text-xs text-slate-500">Loading available {meta.label.toLowerCase()}...</p>
                        )}
                        {key === 'procuringTypes' && (organizationTypesLoading || multiSelectOptions[key].length === 0) && (
                          <p className="text-xs text-slate-500">Loading available {meta.label.toLowerCase()}...</p>
                        )}
                      </>
                    ) : (
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Add {meta.label}</label>
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            value={draftKeyword}
                            placeholder={meta.placeholder}
                            onChange={(event) => setDraftKeyword(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleAddKeyword();
                              }
                            }}
                            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={handleAddKeyword}
                            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {preferences[key].length === 0 ? (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">No {meta.label.toLowerCase()} saved yet</span>
                    ) : (
                      preferences[key].map((item) => (
                        <span
                          key={item}
                          className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() => handleRemovePreference(key, item)}
                            className="text-blue-400 transition hover:text-blue-600"
                            aria-label={`Remove ${item}`}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-blue-50 p-6 sm:flex-row sm:items-center">
        <div><h2 className="text-lg font-semibold text-slate-900">Notification delivery</h2>
          <p className="mt-1 text-sm text-slate-600">Manage email and in-app notification settings in your Account.</p></div>
        <Link to="/account#notifications" className="shrink-0 text-sm font-semibold text-blue-600 hover:underline">Manage notifications →</Link>
      </section>

      <footer className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <AdjustmentsHorizontalIcon className="h-5 w-5 text-slate-400" />
          {changesPending ? 'Unsaved changes' : 'Preferences are up to date'}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleReset}
            disabled={!changesPending || isSaving}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!changesPending || isSaving}
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : 'Save Match Preferences'}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Preferences;
